import { styled, keyframes } from "@pigment-css/react";
import { SidePanelContainer } from "../ui";
import { Flex } from "../../primitives/Flex";
import { SectionDivider } from "../../primitives";

const shimmer = keyframes({
  "0%": { backgroundPosition: "-200px 0" },
  "100%": { backgroundPosition: "200px 0" },
});

const SkeletonElement = styled("div")(({ theme }) => ({
  borderRadius: theme.managementView.radius[10],
  background: `linear-gradient(90deg, ${theme.colors.neutral[200]} 0%, ${theme.colors.neutral[100]} 50%, ${theme.colors.neutral[200]} 100%)`,
  backgroundSize: "400px 100%",
  animation: `${shimmer} 1.5s ease-in-out infinite`,
}));

const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[24],
}));

// Header skeletons
const HeaderTitleSkeleton = styled(SkeletonElement)({
  height: "28px",
  width: "70%",
});

const HeaderSubtitleSkeleton = styled(SkeletonElement)({
  height: "20px",
  width: "40%",
});

// Metrics skeletons
const MetricsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.managementView.spacing[16],
}));

const MetricCard = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[8],
  padding: theme.managementView.spacing[16],
  backgroundColor: theme.managementView.colors.neutral[4],
  borderRadius: theme.managementView.radius[10],
}));

const MetricLabelSkeleton = styled(SkeletonElement)({
  height: "14px",
  width: "60px",
});

const MetricValueSkeleton = styled(SkeletonElement)({
  height: "24px",
  width: "90px",
});

const MetricChangeSkeleton = styled(SkeletonElement)({
  height: "16px",
  width: "50px",
});

// Info section skeletons
const InfoGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.managementView.spacing[32],
  columnGap: theme.managementView.spacing[16],
}));

const InfoItem = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[4],
}));

const InfoLabelSkeleton = styled(SkeletonElement)({
  height: "14px",
  width: "80px",
});

const InfoValueSkeleton = styled(SkeletonElement)({
  height: "20px",
  width: "100px",
});

// Recap section skeletons
const RecapContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[12],
}));

const RecapHeaderSkeleton = styled(SkeletonElement)({
  height: "18px",
  width: "120px",
});

const RecapContentSkeleton = styled(SkeletonElement)({
  height: "60px",
  width: "100%",
});

const RecapCountSkeleton = styled(SkeletonElement)({
  height: "16px",
  width: "140px",
});

// Chart section skeletons
const ChartHeaderSkeleton = styled(SkeletonElement)({
  height: "18px",
  width: "100px",
});

const ChartSkeleton = styled(SkeletonElement)({
  height: "200px",
  width: "100%",
  marginTop: "12px",
});

export const CustomerDetailsSidePanelSkeleton = () => {
  return (
    <SidePanelContainer>
      <Flex direction="column" gap={24}>
        {/* Header skeleton */}
        <Flex direction="column" gap={4}>
          <HeaderTitleSkeleton />
          <HeaderSubtitleSkeleton />
        </Flex>

        <ContentWrapper>
          {/* Metrics section skeleton */}
          <MetricsGrid>
            <MetricCard>
              <MetricLabelSkeleton />
              <MetricValueSkeleton />
              <MetricChangeSkeleton />
            </MetricCard>
            <MetricCard>
              <MetricLabelSkeleton />
              <MetricValueSkeleton />
              <MetricChangeSkeleton />
            </MetricCard>
          </MetricsGrid>

          {/* Info section skeleton */}
          <InfoGrid>
            <InfoItem>
              <InfoLabelSkeleton />
              <InfoValueSkeleton />
            </InfoItem>
            <InfoItem>
              <InfoLabelSkeleton />
              <InfoValueSkeleton />
            </InfoItem>
            <InfoItem>
              <InfoLabelSkeleton />
              <InfoValueSkeleton />
            </InfoItem>
            <InfoItem>
              <InfoLabelSkeleton />
              <InfoValueSkeleton />
            </InfoItem>
          </InfoGrid>

          <SectionDivider />

          {/* Recap section skeleton */}
          <RecapContainer>
            <RecapHeaderSkeleton />
            <RecapContentSkeleton />
            <RecapCountSkeleton />
          </RecapContainer>

          {/* Overview chart skeleton */}
          <Flex direction="column" gap={12}>
            <ChartHeaderSkeleton />
            <ChartSkeleton />
          </Flex>

          <SectionDivider />

          {/* Products chart skeleton */}
          <Flex direction="column" gap={12}>
            <ChartHeaderSkeleton />
            <ChartSkeleton />
          </Flex>

          <SectionDivider />
        </ContentWrapper>
      </Flex>
    </SidePanelContainer>
  );
};
